// src/pages/driverReports/managementReports/idleReportUtils.js

// Normalize to local date (no timezone shift)
export function normalizeDay(d) {
  const x = d instanceof Date ? d : new Date(d);
  return new Date(x.getFullYear(), x.getMonth(), x.getDate());
}

export function monthToRange(monthStr) {
  // monthStr: "YYYY-MM"
  const [y, m] = monthStr.split("-").map(Number);
  const start = new Date(y, m - 1, 1);
  const end = new Date(y, m, 0); // last day of month
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

export function toKey(d) {
  const x = normalizeDay(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(
    x.getDate()
  ).padStart(2, "0")}`;
}

export function daysBetweenInclusive(start, end) {
  const s = normalizeDay(start).getTime();
  const e = normalizeDay(end).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  if (e < s) return 0;
  return Math.floor((e - s) / dayMs) + 1;
}

export function clampInterval(interval, rangeStart, rangeEnd) {
  const s = normalizeDay(interval.start);
  const e = interval.end ? normalizeDay(interval.end) : normalizeDay(rangeEnd); // ongoing => treat as rangeEnd
  const rs = normalizeDay(rangeStart);
  const re = normalizeDay(rangeEnd);

  const start = s < rs ? rs : s;
  const end = e > re ? re : e;

  if (end < start) return null;
  return { start, end };
}

// Merge overlapping/adjacent intervals
export function mergeIntervals(intervals) {
  const sorted = (intervals || [])
    .filter(Boolean)
    .map((x) => ({ start: normalizeDay(x.start), end: normalizeDay(x.end) }))
    .sort((a, b) => a.start - b.start);

  const out = [];
  for (const cur of sorted) {
    if (!out.length) {
      out.push(cur);
      continue;
    }
    const last = out[out.length - 1];
    // adjacent/overlap: last.end + 1day >= cur.start
    const dayMs = 24 * 60 * 60 * 1000;
    if (last.end.getTime() + dayMs >= cur.start.getTime()) {
      if (cur.end > last.end) last.end = cur.end;
    } else {
      out.push(cur);
    }
  }
  return out;
}

export function buildCarIntervals(assignmentsMap) {
  // assignmentsMap = { driverId: [ { vehicleNumber, startDate, endDate, company, vehicleType, driverName } ] }
  const byCar = new Map();

  Object.values(assignmentsMap || {}).forEach((list) => {
    (list || []).forEach((a) => {
      const car = a.vehicleNumber ? String(a.vehicleNumber) : "";
      if (!car) return;

      if (!byCar.has(car)) byCar.set(car, []);
      byCar.get(car).push({
        start: a.startDate,
        end: a.endDate,
        company: a.company || "",
        vehicleType: a.vehicleType || "",
      });
    });
  });

  return byCar; // Map(carNumber -> intervals[])
}

export function buildDriverIntervals(assignmentsMap) {
  // Just convert assignmentsMap to intervals per driverId
  const byDriver = new Map();

  Object.entries(assignmentsMap || {}).forEach(([driverId, list]) => {
    const intervals = (list || [])
      .filter((a) => a.startDate)
      .map((a) => ({ start: a.startDate, end: a.endDate, driverName: a.driverName || "", company: a.company || "" }));
    byDriver.set(driverId, intervals);
  });

  return byDriver; // Map(driverId -> intervals[])
}

export function computeIdleFromIntervals({ intervals, rangeStart, rangeEnd }) {
  // intervals = [{start, end}] within whole history
  const clamped = (intervals || [])
    .map((x) => clampInterval(x, rangeStart, rangeEnd))
    .filter(Boolean);

  const merged = mergeIntervals(clamped);

  const totalDays = daysBetweenInclusive(rangeStart, rangeEnd);

  // assignedDays = sum merged lengths
  let assignedDays = 0;
  merged.forEach((m) => (assignedDays += daysBetweenInclusive(m.start, m.end)));

  const idleDays = Math.max(0, totalDays - assignedDays);

  // Build idle segments = gaps between merged intervals
  const idleSegments = [];
  const rs = normalizeDay(rangeStart);
  const re = normalizeDay(rangeEnd);

  if (!merged.length) {
    idleSegments.push({ start: rs, end: re });
  } else {
    // gap before first
    if (merged[0].start > rs) {
      const end = new Date(merged[0].start);
      end.setDate(end.getDate() - 1);
      idleSegments.push({ start: rs, end });
    }
    // gaps between
    for (let i = 0; i < merged.length - 1; i++) {
      const a = merged[i];
      const b = merged[i + 1];
      const start = new Date(a.end);
      start.setDate(start.getDate() + 1);
      const end = new Date(b.start);
      end.setDate(end.getDate() - 1);
      if (end >= start) idleSegments.push({ start, end });
    }
    // gap after last
    if (merged[merged.length - 1].end < re) {
      const start = new Date(merged[merged.length - 1].end);
      start.setDate(start.getDate() + 1);
      idleSegments.push({ start, end: re });
    }
  }

  return {
    totalDays,
    assignedDays,
    idleDays,
    utilization: totalDays ? assignedDays / totalDays : 0,
    idleSegments,
    mergedAssignedSegments: merged,
  };
}

export function formatSeg(seg) {
  const s = toKey(seg.start);
  const e = toKey(seg.end);
  return s === e ? s : `${s} → ${e}`;
}